// Lazy import to avoid circular dependency: BaseApiService -> Toasts -> Notifications -> BaseNotary -> BaseApiService
import { EventEmitter } from "@Front/Services/EventEmitter";
import type { ReactNode } from "react";
// import I18n from "Components/Elements/I18n";

export enum EToastPriority {
	HIGH = "high",
	LOW = "low",
}

export interface IToast {
	uid?: string;
	redirectUrl?: string;
	id?: number;
	title: string | ReactNode;
	icon?: ReactNode;
	text?: string | ReactNode;
	button?: ReactNode;
	time?: number;
	closable?: boolean;
	priority?: EToastPriority;
	created_at?: Date | null;
}

class Toasts {
	private static _instance: Toasts | null = null;
	private _event: EventEmitter | null = null;
	private toastList: IToast[] = [];
	private uid = 0;

	private get event(): EventEmitter {
		if (!this._event) {
			this._event = new EventEmitter();
		}
		return this._event;
	}

	private defaultTime: IToast["time"] = 0;
	private defaultClosable: IToast["closable"] = true;
	private defaultPriority: IToast["priority"] = EToastPriority.LOW;

	private constructor() {}

	public static getInstance(): Toasts {
		if (!Toasts._instance) {
			Toasts._instance = new Toasts();
		}
		return Toasts._instance;
	}

	public get toasts(): IToast[] {
		return this.toastList;
	}

	/**
	 * @returns removelistener callback
	 */
	public onChange(callback: (toastList: IToast[]) => void): () => void {
		this.event.on("change", callback);
		return () => this.event.off("change", callback);
	}

	public open(toast: IToast): () => void | undefined {
		const toastExists = this.toastList.find((t) => t.uid === toast.uid);
		if (toastExists) return () => undefined;
		const index = this.toastList.indexOf(toast);
		if (index !== -1) return () => this.close(toast);

		toast.id = toast.id ?? this.uid++;
		toast.time = toast.time ?? this.defaultTime;
		toast.closable = toast.closable ?? this.defaultClosable;
		toast.priority = toast.priority ?? this.defaultPriority;

		const highToasts = this.toastList.filter((toast) => {
			return toast.priority === EToastPriority.HIGH;
		});

		const lowToasts = this.toastList.filter((toast) => {
			return toast.priority === EToastPriority.LOW;
		});

		if (toast.priority === EToastPriority.HIGH) {
			highToasts.unshift(toast);
		} else {
			lowToasts.unshift(toast);
		}

		this.toastList.splice(0);
		this.toastList.unshift(...lowToasts);
		this.toastList.unshift(...highToasts);

		this.event.emit("change", this.toastList);

		return () => this.close(toast);
	}

	public markRead(toast: IToast): void {
		if (!toast.uid) return;
		void import("@Front/Api/LeCoffreApi/Notary/Notifications/Notifications")
			.then((m) => m.default.getInstance().put(toast.uid as string, { read: true }))
			.catch(() => {
				// Notifications API optional at init
			});
	}

	public close(toast: IToast): void {
		const index = this.toastList.indexOf(toast);
		if (index === -1) return;
		this.toastList.splice(index, 1);

		this.event.emit("change", this.toastList);
	}

	public async closeAll(): Promise<void> {
		const Notifications = (await import("@Front/Api/LeCoffreApi/Notary/Notifications/Notifications")).default;
		for (let i = 0; i < this.toastList.length; i++) {
			await Notifications.getInstance().put(this.toastList[i]?.uid as string, {
				read: true,
			});
		}
		this.toastList.splice(0, this.toastList.length);
		this.event.emit("change", this.toastList);
	}

	/**
	 * An utility static method you can use to quickly display an error toast
	 * with a custom error message.
	 *
	 * @param message_key a key to a l18n message.
	 */
	public static errorToast(messageKey: string): () => void {
		return Toasts.getInstance().open({
			text: messageKey,
			title: "Toast title",
			// text: <I18n map={message_key} />,
			// title: <I18n map="toast.an_error_occured" />,
			closable: true,
			priority: EToastPriority.HIGH,
		});
	}
}

// Export named only - no default export to avoid Webpack initialization order issues
export { Toasts };
